import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, AlertTriangle, CheckCircle, Users, Lock, Eye, FileText, Phone } from 'lucide-react';
import { Button } from '../../components/shared';
import { SafetySkeleton } from '../../components/shared/ClientPageSkeleton';
import { siteSettingsService } from '../../services/siteSettingsService';

const ICON_MAP = { Shield, AlertTriangle, CheckCircle, Users, Lock, Eye, FileText, Phone };

const Safety = () => {
  const [activeTab, setActiveTab] = useState('guidelines');

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const ss = settingsResponse?.data?.safety_settings;
  const content = useMemo(() => ({
    hero: { title: ss?.hero?.title ?? '', subtitle: ss?.hero?.subtitle ?? '' },
    guidelines: Array.isArray(ss?.guidelines) ? ss.guidelines : [],
    verification: Array.isArray(ss?.verification) ? ss.verification : [],
    reporting: Array.isArray(ss?.reporting) ? ss.reporting : [],
    resources: Array.isArray(ss?.resources) ? ss.resources : [],
    emergency: {
      title: ss?.emergency?.title ?? '',
      description: ss?.emergency?.description ?? '',
      phone: ss?.emergency?.phone ?? '',
      reportButtonText: ss?.emergency?.reportButtonText ?? '',
    },
  }), [ss]);

  if (isLoading) {
    return <SafetySkeleton />;
  }

  const tabs = [
    { id: 'guidelines', label: 'Safety Guidelines', icon: Shield },
    { id: 'verification', label: 'Vendor Verification', icon: CheckCircle },
    { id: 'reporting', label: 'Reporting Issues', icon: AlertTriangle },
    { id: 'resources', label: 'Resources', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-50 -mt-16">
      {/* Hero Section - extends behind transparent header */}
      <div className="bg-gradient-to-br from-black to-gray-900 text-white pt-16 pb-12 sm:pt-20 sm:pb-16">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 animate-bounce-gentle" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              {content.hero.title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300">
              {content.hero.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-container py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Tabs Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 sm:mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex overflow-x-auto -mb-px px-2 sm:px-4 md:px-6 scrollbar-hide">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative py-3 sm:py-4 px-2 sm:px-3 md:px-6 font-medium text-xs sm:text-sm md:text-base transition-all duration-300 whitespace-nowrap border-b-2 flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ${
                        activeTab === tab.id
                          ? "text-black border-black"
                          : "text-gray-600 border-transparent hover:text-black hover:border-gray-300"
                      }`}
                    >
                      <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 flex-shrink-0" />
                      <span className="hidden xs:inline sm:hidden md:inline">{tab.label}</span>
                      <span className="xs:hidden sm:inline md:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-3 sm:p-4 md:p-6 lg:p-8">
              <div className="space-y-4 sm:space-y-6">
                {activeTab === 'guidelines' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                    {content.guidelines.map((item, index) => {
                      const IconComponent = ICON_MAP[item.icon] || Shield;
                      return (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6 hover:border-gray-300 transition-colors"
                        >
                          <div className="flex items-start sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
                              {item.title}
                            </h3>
                          </div>
                          <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed pl-0 sm:pl-0 md:pl-0">
                            {item.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {activeTab === 'verification' && (
                  <div className="space-y-3 sm:space-y-4 md:space-y-6">
                    {content.verification.map((item, index) => (
                      <div
                        key={index}
                        className="border-l-4 border-black pl-3 sm:pl-4 md:pl-6 py-2 sm:py-3"
                      >
                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'reporting' && (
                  <div className="space-y-3 sm:space-y-4 md:space-y-6">
                    {content.reporting.map((item, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6"
                      >
                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'resources' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                    {content.resources.map((item, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6 hover:border-gray-300 transition-colors"
                      >
                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-sm md:text-base text-gray-700 mb-3 sm:mb-4 leading-relaxed">
                          {item.description}
                        </p>
                        <Button variant="outline" size="sm" className="text-xs sm:text-sm">
                          {item.action}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4 md:p-6">
            <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold text-red-900 mb-1">
                  {content.emergency.title}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-red-800 mb-2 sm:mb-3">
                  {content.emergency.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <Button variant="danger" size="sm" className="w-full sm:w-auto text-xs sm:text-sm">
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                    <span className="break-all sm:break-normal">Emergency: {content.emergency.phone}</span>
                  </Button>
                  <Button variant="outline" size="sm" className="w-full sm:w-auto border-red-300 text-red-700 hover:bg-red-100 text-xs sm:text-sm">
                    {content.emergency.reportButtonText}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Safety;

